import { Injectable, inject } from '@angular/core';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import { FormatService } from './format.service';
import { ChartRenderData } from './models';

Chart.register(...registerables);

Chart.defaults.font.family = "'Segoe UI', system-ui, -apple-system, sans-serif";
Chart.defaults.color        = '#64748b';

@Injectable()
export class ChartService {
  private fmt = inject(FormatService);

  private pie:  Chart | null = null;
  private bar:  Chart | null = null;
  private line: Chart | null = null;

  render(
    pieCtx:  HTMLCanvasElement,
    barCtx:  HTMLCanvasElement,
    lineCtx: HTMLCanvasElement,
    data:    ChartRenderData
  ): void {
    this.destroyAll();
    this.renderPie(pieCtx, data);
    this.renderBar(barCtx, data);
    this.renderLine(lineCtx, data);
  }

  // ── Pie / Doughnut ────────────────────────────────────────────────────────
  private renderPie(ctx: HTMLCanvasElement, data: ChartRenderData): void {
    Chart.getChart(ctx)?.destroy();
    const principal = data.loanAmount;
    const interest  = data.finalSummary.totalInterest;
    const total     = principal + interest;
    const fmt       = this.fmt;

    this.pie = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: [
          `Principal  ${((principal/total)*100).toFixed(1)}%`,
          `Interest   ${((interest/total)*100).toFixed(1)}%`
        ],
        datasets: [{
          data: [principal, interest],
          backgroundColor:      ['#3b82f6', '#ef4444'],
          hoverBackgroundColor: ['#2563eb', '#dc2626'],
          borderColor:  '#ffffff',
          borderWidth:  4,
          hoverOffset:  8
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        cutout: '65%',
        animation: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              font: { size: 11, weight: 'bold' },
              padding: 16,
              usePointStyle: true,
              pointStyle: 'circle',
              color: '#475569'
            }
          },
          tooltip: {
            backgroundColor: '#1e293b',
            titleFont: { size: 12, weight: 'bold' },
            bodyFont:  { size: 12 },
            padding:   12,
            cornerRadius: 8,
            callbacks: {
              label: (ctx: any) => `  ${fmt.currency(ctx.raw as number)}`
            }
          }
        }
      },
      plugins: [{
        id: 'doughnutCentre',
        afterDraw(chart: any) {
          const { ctx: c, chartArea } = chart;
          if (!chartArea) return;
          const cx = (chartArea.left   + chartArea.right)  / 2;
          const cy = (chartArea.top    + chartArea.bottom) / 2;
          c.save();
          c.textAlign    = 'center';
          c.textBaseline = 'middle';
          c.font = '600 11px Segoe UI, system-ui, sans-serif';
          c.fillStyle = '#94a3b8';
          c.fillText('Total Cost', cx, cy - 11);
          c.font = 'bold 13px Segoe UI, system-ui, sans-serif';
          c.fillStyle = '#1e293b';
          c.fillText(
            new Intl.NumberFormat('en-IN', { style:'currency', currency:'INR', maximumFractionDigits:0 }).format(total),
            cx, cy + 10
          );
          c.restore();
        }
      }]
    } as unknown as ChartConfiguration);
  }

  // ── Stacked Bar ───────────────────────────────────────────────────────────
  private renderBar(ctx: HTMLCanvasElement, data: ChartRenderData): void {
    Chart.getChart(ctx)?.destroy();
    const fmt    = this.fmt;
    const years  = data.yearlySchedule;
    const labels = years.map(y => `'${String(y.calendarYear).slice(2)}`);

    const datasets: any[] = [
      {
        label: 'Principal',
        data:  years.map(y => Math.round(y.principal)),
        backgroundColor:      'rgba(59,130,246,0.88)',
        hoverBackgroundColor: '#2563eb',
        borderRadius: { topLeft: 3, topRight: 3 },
        borderSkipped: false,
        stack: 'a'
      },
      {
        label: 'Interest',
        data:  years.map(y => Math.round(y.interest)),
        backgroundColor:      'rgba(239,68,68,0.82)',
        hoverBackgroundColor: '#dc2626',
        borderSkipped: false,
        stack: 'a'
      }
    ];

    if (data.hasPrepayments) {
      datasets.push({
        label: 'Prepayment',
        data:  years.map(y => Math.round(y.prepayment)),
        backgroundColor:      'rgba(16,185,129,0.82)',
        hoverBackgroundColor: '#059669',
        borderRadius: { topLeft: 3, topRight: 3 },
        borderSkipped: false,
        stack: 'a'
      });
    }

    this.bar = new Chart(ctx, {
      type: 'bar',
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 500 },
        interaction: { mode: 'index', intersect: false },
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
            border: { display: false },
            ticks: { font: { size: 10 }, color: '#94a3b8', maxRotation: 0 }
          },
          y: {
            stacked: true,
            grid: { color: 'rgba(0,0,0,.04)', lineWidth: 1 },
            border: { display: false, dash: [4,4] },
            ticks: {
              font: { size: 10 },
              color: '#94a3b8',
              callback: (v) => {
                const n = Number(v);
                if (n >= 10_00_000) return '₹' + (n/10_00_000).toFixed(1) + 'Cr';
                if (n >= 1_00_000)  return '₹' + (n/1_00_000).toFixed(0)  + 'L';
                return '₹' + n;
              }
            }
          }
        },
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              font: { size: 11, weight: 'bold' },
              padding: 14,
              usePointStyle: true,
              pointStyle: 'rect',
              color: '#475569'
            }
          },
          tooltip: {
            backgroundColor: '#1e293b',
            titleFont:  { size: 12, weight: 'bold' },
            bodyFont:   { size: 11 },
            padding:    12,
            cornerRadius: 8,
            callbacks: {
              title: (items: any[]) => `Year ${years[items[0].dataIndex]?.calendarYear}`,
              label: (ctx: any) => `  ${ctx.dataset.label}: ${fmt.currency(ctx.raw as number)}`,
              footer: (items: any[]) => {
                const total = items.reduce((s, i) => s + (i.raw as number), 0);
                return `  Total: ${fmt.currency(total)}`;
              }
            }
          }
        }
      }
    });
  }

  // ── Line: Balance over time ───────────────────────────────────────────────
  private renderLine(ctx: HTMLCanvasElement, data: ChartRenderData): void {
    Chart.getChart(ctx)?.destroy();
    const fmt    = this.fmt;
    const years  = data.yearlySchedule;
    const labels = years.map(y => y.calendarYear);

    // Map base yearly by year for aligned rendering
    const baseMap: Record<number, number> = {};
    data.baseYearly.forEach(y => { baseMap[y.calendarYear] = y.balance; });

    const datasets: any[] = [
      {
        label:              'Without Prepayment',
        data:               labels.map(yr => baseMap[yr] ?? null),
        borderColor:        '#94a3b8',
        borderDash:         [6, 4],
        borderWidth:        2,
        fill:               false,
        tension:            0.38,
        pointRadius:        2.5,
        pointHoverRadius:   6,
        pointBackgroundColor: '#94a3b8',
        spanGaps:           true
      },
      {
        label:              data.hasPrepayments ? 'Balance (With Prepayment)' : 'Outstanding Balance',
        data:               years.map(y => Math.round(y.balance)),
        borderColor:        '#3b82f6',
        borderWidth:        2.5,
        backgroundColor:    'rgba(59,130,246,0.07)',
        fill:               true,
        tension:            0.38,
        pointRadius:        2.5,
        pointHoverRadius:   6,
        pointBackgroundColor: '#3b82f6'
      }
    ];

    if (data.hasPrepayments) {
      let cum = 0;
      datasets.push({
        label:              'Cumulative Prepayments',
        data:               years.map(y => { cum += y.prepayment; return Math.round(cum); }),
        borderColor:        '#10b981',
        borderWidth:        2,
        backgroundColor:    'rgba(16,185,129,0.07)',
        fill:               true,
        tension:            0.38,
        pointRadius:        2.5,
        pointHoverRadius:   6,
        pointBackgroundColor: '#10b981'
      });
    }

    this.line = new Chart(ctx, {
      type: 'line',
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 500 },
        interaction: { mode: 'index', intersect: false },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: { font: { size: 10 }, color: '#94a3b8' }
          },
          y: {
            beginAtZero: true,
            grid:   { color: 'rgba(0,0,0,.04)', lineWidth: 1 },
            border: { display: false, dash: [4,4] },
            ticks: {
              font: { size: 10 },
              color: '#94a3b8',
              callback: (v) => {
                const n = Number(v);
                if (n >= 10_00_000) return '₹' + (n/10_00_000).toFixed(1) + 'Cr';
                if (n >= 1_00_000)  return '₹' + (n/1_00_000).toFixed(0)  + 'L';
                return '₹' + n;
              }
            }
          }
        },
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              font: { size: 11, weight: 'bold' },
              padding: 14,
              usePointStyle: true,
              pointStyle: 'line',
              color: '#475569'
            }
          },
          tooltip: {
            backgroundColor: '#1e293b',
            titleFont:  { size: 12, weight: 'bold' },
            bodyFont:   { size: 11 },
            padding:    12,
            cornerRadius: 8,
            callbacks: {
              label: (ctx: any) => `  ${ctx.dataset.label}: ${fmt.currency(ctx.raw as number)}`
            }
          }
        }
      }
    });
  }

  destroyAll(): void {
    this.pie?.destroy();  this.pie  = null;
    this.bar?.destroy();  this.bar  = null;
    this.line?.destroy(); this.line = null;
  }
}
